import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { InputAdornment, TextField } from '@mui/material';
import { withLocalize } from 'react-localize-redux';

import css from './SearchTextField.css';
import Icon, { IconSizes, IconTypes, IconVariants } from '../../foundations/Icon';

export const SEARCH_PLACEHOLDER = 'STATIC.PLACEHOLDERS.SEARCH';

const InputAdornmentIcon = React.memo(({ iconSize, position, ...props }) => (
    <InputAdornment position={position}>
        <Icon size={iconSize} variant={IconVariants.LIGHT} {...props} />
    </InputAdornment>
));

InputAdornmentIcon.propTypes = {
    iconSize: PropTypes.oneOf(Object.values(IconSizes)),
    position: PropTypes.oneOf(['start', 'end']),
    iconName: PropTypes.string,
    onClick: PropTypes.func,
};

const SearchTextField = ({
    search,
    onSearchChange,
    clearable = false,
    label,
    placeholder = SEARCH_PLACEHOLDER,
    iconSize = IconSizes.LARGE,
    translate,
    variant = 'outlined',
    disabled = false,
    className,
}) => {
    const inputRef = useRef();

    const handleClear = useCallback(() => {
        onSearchChange('');
        inputRef.current?.focus();
    }, [onSearchChange]);

    const onChange = event => {
        event.stopPropagation();
        event.preventDefault();
        onSearchChange(event.target.value);
    };

    return (
        <TextField
            variant={variant}
            size="small"
            fullWidth
            value={search}
            disabled={disabled}
            placeholder={placeholder ? translate(placeholder) : null}
            label={label ? translate(label) : null}
            onChange={onChange}
            onClick={event => event.stopPropagation()}
            className={className}
            inputRef={inputRef}
            InputLabelProps={{
                className: css.label,
            }}
            autoComplete="off"
            InputProps={{
                startAdornment: <InputAdornmentIcon position="start" iconSize={iconSize} name="search-line" />,
                endAdornment: clearable && search && (
                    <InputAdornmentIcon
                        position="end"
                        iconSize={iconSize}
                        type={IconTypes.MINGCUTE}
                        name="close-line"
                        onClick={handleClear}
                    />
                ),
                sx: {
                    fontSize: '14px',
                },
            }}
            sx={{
                '& .MuiOutlinedInput-root': {
                    padding: '0 8px',
                    '& fieldset': {
                        borderColor: 'var(--gray400)',
                    },
                },
                '& .MuiOutlinedInput-root.Mui-disabled': {
                    backgroundColor: 'var(--gray50)',
                },
                '& .MuiOutlinedInput-input.Mui-disabled': {
                    backgroundColor: 'var(--gray50) !important',
                },
                '& .MuiInput-underline': {
                    borderBottomColor: 'var(--gray500)',
                },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                    borderBottomColor: 'var(--gray500)',
                    borderBottomWidth: '1px',
                },
            }}
        />
    );
};

SearchTextField.propTypes = {
    search: PropTypes.string,
    onSearchChange: PropTypes.func,
    clearable: PropTypes.bool,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    iconSize: PropTypes.oneOf(Object.values(IconSizes)),
    translate: PropTypes.func,
    variant: PropTypes.oneOf(['outlined', 'standard', 'filled']),
    className: PropTypes.string,
    disabled: PropTypes.bool,
};

export default withLocalize(SearchTextField);
